import { useCallback, useMemo, useState } from 'react';
import { Modal } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Fulfillment } from 'api/models/Fulfillment';
import { ModelStatus } from 'dashboard409a/pages/Operations/components/ModelStatus';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { ModelStatus as ModelStatusEnum } from 'dashboard409a/pages/Workboard/common/types';
import { useCreateFulfillment } from 'dashboard409a/services/fulfillment';
import { FulfillmentWithCompanyName, ModelWithValuationData } from 'dashboard409a/services/models.types';
import { StyledBox } from '../ModelsTable/ModelsTable.styles';

export const useModelStatusModal = ({ models }: { models: ModelWithValuationData[] }) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<number | undefined>();
  const { enqueueSnackbar } = useSnackbar();

  // Derive the selected model from models reactively
  const selectedModel = useMemo(() => {
    if (!selectedModelId) {
      return undefined;
    }
    return models.find(
      model => model.id === selectedModelId || model.company_measurement_date === selectedModelId
    ) as FulfillmentWithCompanyName | undefined;
  }, [models, selectedModelId]);

  const companyId = selectedModel?.company_id?.toString();
  const createFulfillmentMutation = useCreateFulfillment(companyId);

  const handleOpenModal = useCallback((model: ModelWithValuationData) => {
    // Store the ID or company_measurement_date as identifier
    setSelectedModelId(model.id || model.company_measurement_date);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedModelId(undefined);
  }, []);

  const handleCreateModelSupport = async () => {
    if (!companyId) {
      enqueueSnackbar('Unable to create model support: Company information not available', { 
        variant: 'error' 
      });
      return;
    }

    if (!selectedModel?.company_measurement_date) {
      enqueueSnackbar('Unable to create model support: Measurement date information not available', { 
        variant: 'error' 
      });
      return;
    }

    try {
      await createFulfillmentMutation.mutateAsync({
        company_measurement_date: selectedModel.company_measurement_date,
        model_status: modelStatusMap[ModelStatusEnum.DRAFT],
        supporting_due_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        reviewers: [],
        preparers: [],
      });
    } catch {
      // Error handling is done in the mutation
    }
  };

  return {
    openModal,
    selectedModel,
    handleOpenModal,
    handleCloseModal,
    handleCreateModelSupport,
    isCreatingModelSupport: createFulfillmentMutation.isPending,
  };
};

interface ModelStatusModalComponentProps {
  open: boolean;
  onClose: () => void;
  selectedModel: FulfillmentWithCompanyName | undefined;
  isLoading: boolean;
  onCreateModelSupport: () => void;
  isCreatingModelSupport: boolean;
}

export const ModelStatusModal = ({
  open,
  onClose,
  selectedModel,
  isLoading,
  onCreateModelSupport,
  isCreatingModelSupport,
}: ModelStatusModalComponentProps) => (
  <Modal
    open={open}
    onClose={onClose}
    sx={{
      '&:focus': {
        outline: 'none',
      },
      '&:focus-visible': {
        outline: 'none',
      },
    }}
  >
    <StyledBox 
      onClick={e => e.stopPropagation()}
      sx={{
        outline: 'none',
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
        '&:focus-within': {
          outline: 'none',
        },
      }}
    >
      <ModelStatus
        fulfillmentData={{ results: selectedModel ? [selectedModel as Fulfillment] : [] }}
        isLoading={isLoading}
        onCreateModelSupport={onCreateModelSupport}
        isCreatingModelSupport={isCreatingModelSupport}
        companyId={selectedModel?.company_id?.toString()}
        valuationId={selectedModel?.attio_valuation_id}
        companyMeasurementDateId={selectedModel?.company_measurement_date}
      />
    </StyledBox>
  </Modal>
);

